import { Component } from '@theme/component';

/**
 * @typedef {Object} StickyAnchorNavRefs
 * @property {HTMLAnchorElement[]} links
 */

class StickyAnchorNav extends Component {
  /** @type {IntersectionObserver | null} */
  #scrollSpy = null;

  /** @type {Map<string, HTMLAnchorElement>} */
  #linksByTarget = new Map();

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--anchor-nav-height', `${height}px`);
  });

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#updateStickyTop();
    this.#initScrollSpy();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#scrollSpy?.disconnect();
    document.body.style.setProperty('--anchor-nav-height', '0px');
  }

  /**
   * @param {Event} event
   */
  handleLinkClick(event) {
    const link = event.currentTarget instanceof HTMLAnchorElement
      ? event.currentTarget
      : /** @type {HTMLAnchorElement} */ (event.target);
    const hash = link.getAttribute('href');
    if (!hash?.startsWith('#')) return;

    const targetId = this.#getTargetIdFromHash(hash);
    const target = document.getElementById(targetId);
    if (!target) return;

    event.preventDefault();
    history.pushState(null, '', `#${targetId}`);
    target.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * @param {HTMLAnchorElement} activeLink
   */
  #setActiveLink(activeLink) {
    const links = /** @type {HTMLAnchorElement[]} */ (this.refs.links);
    if (!Array.isArray(links)) return;
    for (const link of links) {
      link.toggleAttribute('data-active', link === activeLink);
    }
  }

  /**
   * Sets the section wrapper's sticky top to match the header's bottom edge.
   */
  #updateStickyTop() {
    const header = document.querySelector('header-component');
    const sectionWrapper = this.closest('.shopify-section');
    if (!header || !sectionWrapper) return;

    const observer = new ResizeObserver(() => {
      const headerBottom = header.getBoundingClientRect().height;
      /** @type {HTMLElement} */ (sectionWrapper).style.top = `${headerBottom - 1}px`;
    });
    observer.observe(header);
  }

  #initScrollSpy() {
    const links = /** @type {HTMLAnchorElement[]} */ (this.refs.links);
    if (!Array.isArray(links)) return;

    /** @type {HTMLElement[]} */
    const targets = [];

    for (const link of links) {
      const href = link.getAttribute('href');
      if (!href?.startsWith('#')) continue;

      const targetId = this.#getTargetIdFromHash(href);
      const target = document.getElementById(targetId);
      if (!target) continue;

      this.#linksByTarget.set(targetId, link);
      targets.push(target);
    }

    if (!targets.length) return;

    this.#scrollSpy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const link = this.#linksByTarget.get(entry.target.id);
          if (!link) continue;
          this.#setActiveLink(link);
        }
      },
      {
        rootMargin: '-20% 0px -70% 0px',
      }
    );

    for (const target of targets) {
      this.#scrollSpy.observe(target);
    }
  }

  /**
   * Normalizes href fragments such as #slug, #/slug or #blog/slug to slug.
   * @param {string} hash
   * @returns {string}
   */
  #getTargetIdFromHash(hash) {
    const rawValue = hash.replace(/^#/, '').replace(/^\/+/, '');
    if (!rawValue.includes('/')) return rawValue;

    const segments = rawValue.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment) return lastSegment;
    return rawValue;
  }
}

customElements.define('sticky-anchor-nav', StickyAnchorNav);
